import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { useCollection } from "../useData";
import { resolveImg } from "../api";
import SEO from "../components/SEO";
import NotFound from "../components/NotFound";

const ExhibitionDetail = () => {
  const { id } = useParams();
  const exhibitions = useCollection("exhibitions") || [];
  const ex = exhibitions.find((e) => e.id === id);
  const [idx, setIdx] = useState(0);

  useEffect(() => { setIdx(0); }, [id]);

  if (!exhibitions.length) return null;
  if (!ex) return <NotFound section="This exhibition" backTo="/exhibitions" backLabel="Back to exhibitions" />;

  const images = ex.images && ex.images.length ? ex.images : [];
  const currentIdx = exhibitions.findIndex((e) => e.id === ex.id);
  const nextEx = exhibitions[(currentIdx + 1) % exhibitions.length];

  const next = () => images.length && setIdx((i) => (i + 1) % images.length);
  const prev = () => images.length && setIdx((i) => (i - 1 + images.length) % images.length);

  return (
    <div className="px-6 md:px-10 max-w-[1100px] mx-auto">
      <SEO
        title={`${ex.title} — ${ex.venue}`}
        description={ex.description || `${ex.title} — ${ex.kind} at ${ex.venue}, ${ex.city}.`}
        image={images[0]}
        path={`/exhibitions/${ex.id}`}
      />
      <div className="pt-2 pb-6 flex items-baseline justify-between">
        <Link to="/exhibitions" className="font-serif tlink tlink-mute">← Index</Link>
        <div className="flex items-baseline gap-4">
          <span className="font-serif italic text-xl">{ex.title}</span>
          <span className="font-serif text-[color:var(--ink-mute)]">{ex.kind} — {ex.city}</span>
        </div>
      </div>

      {/* Gallery — smaller square */}
      <div className="max-w-[700px] mx-auto">
        <div className="relative w-full aspect-square bg-[color:var(--paper-2)] overflow-hidden fade-in">
          {images.map((src, i) => (
            <img
              key={i}
              src={resolveImg(src)}
              alt={`${ex.title} installation view ${i + 1}`}
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${i === idx ? "opacity-100" : "opacity-0"}`}
            />
          ))}
          <button onClick={prev} className="absolute left-0 top-0 h-full w-1/2 z-10" aria-label="Previous" style={{ cursor: "w-resize" }} />
          <button onClick={next} className="absolute right-0 top-0 h-full w-1/2 z-10" aria-label="Next" style={{ cursor: "e-resize" }} />
        </div>

        <div className="mt-4 flex items-baseline justify-between">
          <span className="font-serif text-[color:var(--ink-mute)]">Installation view — {ex.venue}, {ex.city}</span>
          <div className="flex items-baseline gap-4">
            <span className="font-serif text-[color:var(--ink-mute)] counter">
              {String(Math.min(idx + 1, images.length || 1)).padStart(2, "0")} / {String(images.length).padStart(2, "0")}
            </span>
            <div className="flex gap-1.5">
              {images.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setIdx(i)}
                  className={`w-1.5 h-1.5 rounded-full transition-colors ${i === idx ? "bg-[color:var(--ink)]" : "bg-[color:var(--line)]"}`}
                  aria-label={`Image ${i + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Description */}
      <div className="mt-20 max-w-[900px] mx-auto">
        <h1 className="font-serif italic text-4xl md:text-6xl leading-[1.05] mb-6">{ex.title}</h1>
        <p className="font-serif text-xl md:text-2xl leading-[1.35]">{ex.description}</p>
      </div>

      {/* Details */}
      <div className="mt-16 max-w-[900px] mx-auto pb-4">
        <dl className="grid grid-cols-12 gap-y-2 font-serif">
          <dt className="col-span-4 text-[color:var(--ink-mute)]">Dates</dt><dd className="col-span-8">{ex.dates}</dd>
          <dt className="col-span-4 text-[color:var(--ink-mute)]">Venue</dt><dd className="col-span-8">{ex.venue}</dd>
          <dt className="col-span-4 text-[color:var(--ink-mute)]">City</dt><dd className="col-span-8">{ex.city}</dd>
          <dt className="col-span-4 text-[color:var(--ink-mute)]">Kind</dt><dd className="col-span-8">{ex.kind}</dd>
        </dl>
      </div>

      <div className="mt-32 pt-8 border-t border-[color:var(--line)] flex items-baseline justify-between">
        <span className="font-serif italic text-[color:var(--ink-mute)]">Next exhibition</span>
        <Link to={`/exhibitions/${nextEx.id}`} className="font-serif italic text-2xl tlink">{nextEx.title} →</Link>
      </div>
    </div>
  );
};

export default ExhibitionDetail;
